import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { ICategory, ITerm } from "@evennt-tech/api-v2-core";
import { parseTextFromHTML } from "tools/file/parseTextFromHTML";
import { useGlossarySearchQuery } from "store/glossary/glossary.store.selectors";
import getTextWithVariables from "tools/task/getTextWithVariables";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import useStableTranslate from "hooks/useStableTranslate";
import { useLocalMapContext } from "view/context/LocalMapContext";
import { IGetPublicInfoShowRes } from "@evennt-tech/api-v2-external";

export interface ISearchedCategory extends ICategory {
  matchesCount?: number;
}

type Params = {
  terms: ITerm[];
  categories: ICategory[];
  selectedCategory?: ICategory | null;
  setSelectedCategory: Dispatch<SetStateAction<ICategory | null>>;
  show?: IGetPublicInfoShowRes | null;
};

export const useGlossarySearch = ({
  terms,
  categories,
  setSelectedCategory,
  selectedCategory,
  show,
}: Params) => {
  const selectedCategoryId = selectedCategory?.id;
  const searchQuery = useGlossarySearchQuery();
  const { organiser: localizedOrganiser } = useLocalMapContext();
  const { t, language } = useStableTranslate();
  const locale = useDateFnsLocale();

  const { termsMatchingQuery, matchesCountByCategory } = useMemo(() => {
    if (!searchQuery || !show) {
      return { termsMatchingQuery: terms, matchesCountByCategory: {} };
    }

    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedQuery, "gi");
    return terms.reduce<{
      termsMatchingQuery: ITerm[];
      matchesCountByCategory: Record<string, number>;
    }>(
      (acc, curr) => {
        const { description, title, categoryId } = curr;
        let titleMatches = title.match(regex)?.length ?? 0;
        let currentCategoryCount = acc.matchesCountByCategory[categoryId] ?? 0;
        if (titleMatches) {
          currentCategoryCount += titleMatches;
        }
        const sanitizedDescription = parseTextFromHTML(
          getTextWithVariables({
            description,
            show,
            localizedOrganiser,
            t,
            locale,
          })
        );

        const descriptionMatches =
          sanitizedDescription.match(regex)?.length ?? 0;
        if (descriptionMatches) {
          currentCategoryCount += descriptionMatches;
        }

        if (titleMatches || descriptionMatches) {
          acc.matchesCountByCategory[categoryId] = currentCategoryCount;
          acc.termsMatchingQuery.push(curr);
        }
        return acc;
      },
      { termsMatchingQuery: [], matchesCountByCategory: {} }
    );
  }, [terms, searchQuery, show, localizedOrganiser, language, locale]);

  const availableCategories = useMemo<ISearchedCategory[]>(() => {
    if (!searchQuery) {
      return categories;
    }

    return categories.reduce<ISearchedCategory[]>((acc, cat) => {
      const categoryMatchesCount = matchesCountByCategory[cat.id];
      if (categoryMatchesCount) {
        acc.push({ ...cat, matchesCount: categoryMatchesCount });
      }
      return acc;
    }, []);
  }, [categories, searchQuery, matchesCountByCategory]);

  const termsMatchingCategory = useMemo(() => {
    if (!selectedCategoryId) {
      return termsMatchingQuery;
    }

    return termsMatchingQuery.filter(
      (t) => t.categoryId === selectedCategoryId
    );
  }, [termsMatchingQuery, selectedCategoryId]);

  useEffect(() => {
    if (!searchQuery || !setSelectedCategory) {
      return;
    }
    if (!availableCategories.length) {
      return setSelectedCategory(null);
    }

    if (selectedCategoryId) {
      const currentlySelectedMatchingCategory = availableCategories.find(
        (cat) => cat.id === selectedCategoryId
      );
      if (!currentlySelectedMatchingCategory) {
        return setSelectedCategory(availableCategories[0] ?? null);
      }
      return;
    }

    setSelectedCategory(availableCategories[0] ?? null);
  }, [
    searchQuery,
    selectedCategoryId,
    availableCategories,
    setSelectedCategory,
  ]);

  return {
    terms: termsMatchingCategory,
    categories: availableCategories,
  } as const;
};
